import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Calendar, TrendingUp, Mail, Brain, Zap, Users, Rocket, DollarSign, Target, Shield, BarChart3, Globe, Award, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const InvestorDeck = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const slides = [
    // Title Slide
    {
      id: 'title',
      content: (
        <div className="flex flex-col items-center justify-center h-full text-center px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="inline-flex items-center justify-center p-3 bg-gradient-to-r from-purple-600/10 to-blue-600/10 rounded-2xl mb-4">
              <Rocket className="w-12 h-12 text-purple-600" />
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold bg-gradient-to-r from-purple-600 via-blue-600 to-purple-600 bg-clip-text text-transparent leading-tight">
              MUNGER AI
            </h1>
            
            <p className="text-2xl md:text-3xl text-gray-700 font-medium max-w-4xl mx-auto leading-relaxed">
              By Automating Online Marketing, Munger Will Empower Founders 
              To Change The World With Product Obsession
            </p>
            
            <div className="pt-8">
              <Badge className="px-6 py-2 text-lg bg-gradient-to-r from-purple-600 to-blue-600 text-white border-0">
                A World Where Manual Marketing Is Optional
              </Badge>
            </div>
          </motion.div>
        </div>
      )
    },

    // Problem Slide
    {
      id: 'problem',
      content: (
        <div className="flex flex-col h-full px-8 py-12">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="p-2 bg-red-100 rounded-lg">
                <Target className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-4xl font-bold text-gray-900">
                Problem: B2B Founders Become Their Own Worst BDR
              </h2>
            </div>

            <p className="text-xl text-gray-700 mb-8">
              B2B founders know their product is what changes the world, and lead generation is a necessary evil. 
              Yet today's methods trap founders, divert focus and risk a death spiral.
            </p>

            <div className="grid gap-6">
              {[
                {
                  icon: '💸',
                  method: 'Paid Ads',
                  cost: '$5-8K/mo on clicks that rarely convert',
                  issue: 'Every test is a money burn, requires constantly creating & launching new creatives, requires careful cash flow management, becomes a part time job.'
                },
                {
                  icon: '🛠',
                  method: 'Hire SDRs',
                  cost: '$11K/mo before they book a single meeting',
                  issue: 'Cash out before pipeline in'
                },
                {
                  icon: '🎲',
                  method: 'Pray for Inbound',
                  cost: 'Competitors book 50+ meetings/mo while you wait',
                  issue: 'You lose mind-share and market-share'
                }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.2 }}
                  className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow"
                >
                  <div className="flex items-start gap-4">
                    <span className="text-3xl">{item.icon}</span>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{item.method}</h3>
                      <p className="text-red-600 font-semibold mb-2">{item.cost}</p>
                      <p className="text-gray-600 text-sm">{item.issue}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      )
    },

    // Solution Slide
    {
      id: 'solution',
      content: (
        <div className="flex flex-col h-full px-8 py-12">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-8"
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="p-2 bg-green-100 rounded-lg">
                <Brain className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-4xl font-bold text-gray-900">
                Solution: An AI that runs your entire outbound marketing stack
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  number: '1',
                  title: 'Understands Your Business',
                  description: 'Learns your product, ICP, and value prop in 30 seconds.',
                  detail: 'Builds expert-level context like a $150k/year SDR team.'
                },
                {
                  number: '2',
                  title: 'Executes Every Step Automatically',
                  description: 'From research → list building → personalized outreach → booked meetings.',
                  detail: '90%+ automation: runs while founders sleep.'
                },
                {
                  number: '3',
                  title: 'Continuously Self-Improves',
                  description: 'AI "bandit" engine optimizes based on KPIs.',
                  detail: 'Data flywheel improves results with every campaign.'
                }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.2 }}
                  className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6 border border-purple-200"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                      {item.number}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">{item.title}</h3>
                  </div>
                  <p className="text-gray-700 mb-2">{item.description}</p>
                  <p className="text-sm text-gray-600">{item.detail}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      )
    },

    // Why Cold Email Slide
    {
      id: 'why-email',
      content: (
        <div className="flex flex-col h-full px-8 py-12">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-8"
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Mail className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-4xl font-bold text-gray-900">
                Why start with cold email?
              </h2>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8 border border-blue-200">
              <p className="text-xl text-gray-800 leading-relaxed mb-6">
                With the right infrastructure, you can reach your entire market, with the right message, 
                as many times as you want, at the cost of cloud while being fully compliant.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-md">
                  <div className="flex items-center gap-3 mb-3">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                    <h3 className="text-lg font-bold text-gray-900">47:1 ROI</h3>
                  </div>
                  <p className="text-gray-600">
                    Average return on investment according to UK Data & Marketing Association
                  </p>
                </div>
                
                <div className="bg-white rounded-xl p-6 shadow-md">
                  <div className="flex items-center gap-3 mb-3">
                    <Brain className="w-6 h-6 text-purple-600" />
                    <h3 className="text-lg font-bold text-gray-900">Self-Learning</h3>
                  </div>
                  <p className="text-gray-600">
                    Munger tracks what emails generate leads and optimizes campaigns in real time
                  </p>
                </div>
              </div>

              <p className="text-gray-700 mt-6">
                This training set can be universalized for other forms of cold outreach. 
                Retrieving a high volume of meaningful data (conversions) at low cost makes email a unique platform.
              </p>
            </div>
          </motion.div>
        </div>
      )
    },

    // Product Slide
    {
      id: 'product',
      content: (
        <div className="flex flex-col h-full px-8 py-12">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Zap className="w-8 h-8 text-purple-600" />
              </div>
              <h2 className="text-4xl font-bold text-gray-900">
                Product — The Self-Driving Outbound Engine
              </h2>
            </div>

            <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl p-6 mb-6">
              <p className="text-xl font-bold">
                30 seconds of input. 5-15 qualified meetings every week. Zero management.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {[
                { icon: '📝', title: 'Business Resume (30 sec)', desc: 'Founder types one sentence (product + ICP)' },
                { icon: '🌐', title: 'Pre-Warmed Domains', desc: '70+ domains → 25-45% open-rate (vs ≈27% industry)' },
                { icon: '🔍', title: 'Prospector', desc: 'Pulls & verifies targets from 250M+ contacts' },
                { icon: '✍️', title: 'AI Copywriter', desc: 'Reads LinkedIn, crafts 1-of-1 personalized emails' },
                { icon: '🚀', title: 'Autopilot Sender', desc: 'Scales to 150k emails/month/user (10M/day network)' },
                { icon: '📊', title: 'Performance Engine', desc: 'Multi-armed bandit optimizes campaigns 24/7' }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-4 shadow-md border border-blue-500/20"
                >
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{item.icon}</span>
                    <div>
                      <h4 className="font-bold text-white">{item.title}</h4>
                      <p className="text-sm text-blue-200">{item.desc}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="bg-green-500/10 rounded-xl p-6 border border-green-500/30">
              <h3 className="text-xl font-bold text-white mb-4">Proven Results:</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-green-400">3.5M</p>
                  <p className="text-sm text-blue-200">Emails Sent</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-400">7,200</p>
                  <p className="text-sm text-blue-200">Meetings</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-400">40k</p>
                  <p className="text-sm text-blue-200">Leads</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-400">$5</p>
                  <p className="text-sm text-blue-200">Per Meeting</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )
    },

    // Business Model Slide
    {
      id: 'business-model',
      content: (
        <div className="flex flex-col h-full px-8 py-12">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-8"
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-4xl font-bold text-gray-900">
                Business Model
              </h2>
            </div>

            <p className="text-xl text-gray-700">
              Pay based on email volume, everything else included at no cost.
            </p>

            <div className="grid md:grid-cols-4 gap-6">
              {[
                { plan: 'Growth', emails: '150,000/mo', price: '$97/mo', margin: '94.7%' },
                { plan: 'Scale', emails: '500,000/mo', price: '$297/mo', margin: '93.9%' },
                { plan: 'Custom', emails: '1,500,000/mo', price: '$999/mo', margin: '94.1%' },
                { plan: 'Enterprise', emails: 'Up To 1M/Day', price: 'Custom', margin: 'Comparable' }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className={`rounded-xl p-6 border-2 ${
                    idx === 0 ? 'bg-gradient-to-br from-purple-50 to-blue-50 border-purple-300' : 'bg-white border-gray-200'
                  }`}
                >
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{item.plan}</h3>
                  <p className="text-3xl font-bold text-purple-600 mb-2">{item.price}</p>
                  <p className="text-sm text-gray-600 mb-4">{item.emails}</p>
                  <div className="pt-4 border-t">
                    <p className="text-sm text-gray-500">Gross Margin</p>
                    <p className="font-bold text-green-600">{item.margin}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
              <p className="text-gray-700">
                As users succeed at lower plans they will scale up to higher plans ultimately to generate more leads.
              </p>
            </div>
          </motion.div>
        </div>
      )
    },

    // Technology & Moat Slide
    {
      id: 'moat',
      content: (
        <div className="flex flex-col h-full px-8 py-12">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Shield className="w-8 h-8 text-purple-600" />
              </div>
              <h2 className="text-4xl font-bold text-gray-900">
                Our Technology & Moat
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Globe className="w-5 h-5 text-blue-600" />
                    300M Contact Database
                  </h3>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                      <span>What made Apollo a $2B company we give users at no cost</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                      <span>27 datapoints in 80 countries before enrichment</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                      <span>Would require millions of dollars to duplicate</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Mail className="w-5 h-5 text-purple-600" />
                    Proprietary Email Infrastructure
                  </h3>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                      <span>Averages 27% open rates with cold email</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                      <span>Prevents blacklisting, high domain & IP reputation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                      <span>$80 per 30 million emails sent</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Brain className="w-5 h-5 text-green-600" />
                    Complex Architecture
                  </h3>
                  <p className="text-gray-600 mb-3">
                    Munger requires deep expertise in:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {['Email Systems', 'AI Agents', 'Marketing Strategy', 'Multi-Armed Bandits', 'RAG', 'Database Engineering', 'Events Architecture'].map((tech) => (
                      <Badge key={tech} variant="secondary">{tech}</Badge>
                    ))}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6 border border-purple-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Market Experience</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• $1.2M in 2 years selling email as a service</li>
                    <li>• Sold 100,000 emails/day for $25,000 packages</li>
                    <li>• Experience from 7,000 sales meetings</li>
                    <li>• Deeper understanding than 99% of teams</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )
    },

    // Team Slide
    {
      id: 'team',
      content: (
        <div className="flex flex-col h-full px-8 py-12">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-8"
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-4xl font-bold text-gray-900">
                Team
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  name: 'Tristen Larsen',
                  role: 'CEO / Growth',
                  achievements: [
                    '2× 8-figure marketing agency exits',
                    'Scaled Blockem.io → 410k users in 6 mo',
                    'Sent 100M+ cold emails, $1.2M ARR via email-as-a-service'
                  ]
                },
                {
                  name: 'Igor Ferreira',
                  role: 'CTO',
                  achievements: [
                    'CS, ITA ("Brazil\'s MIT")',
                    'Built email infra sending 20M msgs/day',
                    'Expert in deliverability & distributed systems'
                  ]
                },
                {
                  name: 'Rish Kapoor',
                  role: 'Founding Engineer',
                  achievements: [
                    'ex-Uber Eng Lead, scaled rider infra to 200M+ users',
                    'Head of Eng @ unicorn $1B+ valuation',
                    'Specialised in ML-driven optimisation engines'
                  ]
                }
              ].map((member, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.2 }}
                  className="bg-white rounded-xl p-6 shadow-lg border border-gray-200"
                >
                  <div className="w-20 h-20 bg-gradient-to-br from-purple-400 to-blue-400 rounded-full mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 text-center mb-2">{member.name}</h3>
                  <p className="text-purple-600 font-semibold text-center mb-4">{member.role}</p>
                  <ul className="space-y-2">
                    {member.achievements.map((achievement, i) => (
                      <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{achievement}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      )
    },

    // Traction Slide
    {
      id: 'traction',
      content: (
        <div className="flex flex-col h-full px-8 py-12">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-8"
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="p-2 bg-green-100 rounded-lg">
                <BarChart3 className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-4xl font-bold text-gray-900">
                Traction in Beta (Since July 1st 2025)
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { metric: '120', label: 'Free Trial Users', icon: Users },
                { metric: '$5,000', label: 'Enterprise Setup Fees', icon: DollarSign },
                { metric: '$5,850', label: 'Monthly Recurring Revenue', icon: TrendingUp },
                { metric: '3-4', label: 'Demos Per Day (AVG)', icon: Calendar },
                { metric: '$10,850', label: 'First Month Sales', icon: Award },
                { metric: '2x', label: 'MRR Growth Week over Week', icon: Rocket }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200"
                >
                  <item.icon className="w-8 h-8 text-green-600 mb-3" />
                  <p className="text-3xl font-bold text-gray-900 mb-2">{item.metric}</p>
                  <p className="text-gray-600">{item.label}</p>
                </motion.div>
              ))}
            </div>

            <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Growth Trajectory</h3>
              <div className="h-40 flex items-end justify-between gap-2">
                {[30, 45, 60, 80, 100, 140, 200].map((height, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ height: 0 }}
                    animate={{ height: `${height}%` }}
                    transition={{ delay: idx * 0.1, duration: 0.5 }}
                    className="flex-1 bg-gradient-to-t from-purple-600 to-blue-600 rounded-t-lg"
                  />
                ))}
              </div>
              <p className="text-center text-gray-600 mt-3">MRR doubling week over week</p>
            </div>
          </motion.div>
        </div>
      )
    },

    // Distribution Slide
    {
      id: 'distribution',
      content: (
        <div className="flex flex-col h-full px-8 py-12">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Globe className="w-8 h-8 text-purple-600" />
              </div>
              <h2 className="text-4xl font-bold text-gray-900">
                Distribution Strategy
              </h2>
            </div>

            <div className="space-y-6">
              {[
                {
                  phase: 'Phase 1',
                  title: 'Cold Outbound (What\'s working now)',
                  items: [
                    'Cold email: Up to 1M emails/day → 3-8 demos daily',
                    'Cold DMs: 30 paid users this month'
                  ],
                  color: 'blue'
                },
                {
                  phase: 'Phase 2',
                  title: 'Virality, Branding & Cold Outbound',
                  items: [
                    'Go on friend\'s podcasts',
                    'Launch viral video',
                    'Incentivize influencers to reshare',
                    'Turn 5 existing users into 6 new users'
                  ],
                  color: 'purple'
                },
                {
                  phase: 'Phase 3',
                  title: 'All of the above & Paid Ads',
                  items: [
                    'Tristen: 6 years experience with paid ads',
                    'Spent over $7M in Facebook ads targeting business owners'
                  ],
                  color: 'green'
                }
              ].map((phase, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.2 }}
                  className={`bg-gradient-to-r ${
                    phase.color === 'blue' ? 'from-blue-50 to-indigo-50 border-blue-200' :
                    phase.color === 'purple' ? 'from-purple-50 to-pink-50 border-purple-200' :
                    'from-green-50 to-emerald-50 border-green-200'
                  } rounded-xl p-6 border`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <Badge className={`${
                      phase.color === 'blue' ? 'bg-blue-600' :
                      phase.color === 'purple' ? 'bg-purple-600' :
                      'bg-green-600'
                    } text-white`}>
                      {phase.phase}
                    </Badge>
                    <h3 className="text-xl font-bold text-gray-900">{phase.title}</h3>
                  </div>
                  <ul className="space-y-2">
                    {phase.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-gray-700">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      )
    },

    // Why Now Slide
    {
      id: 'why-now',
      content: (
        <div className="flex flex-col h-full px-8 py-12">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-8"
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Zap className="w-8 h-8 text-orange-600" />
              </div>
              <h2 className="text-4xl font-bold text-gray-900">
                Why Now?
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {[
                {
                  stat: '-80%',
                  label: 'GPT-4-o cost YoY',
                  source: 'OpenAI, May-25',
                  color: 'green'
                },
                {
                  stat: '+32%',
                  label: 'SDR cost since 2021',
                  source: 'Bridge Group 2025',
                  color: 'red'
                },
                {
                  stat: '52%',
                  label: 'CMOs deploying Gen-AI',
                  source: 'Deloitte CMO Survey 2025',
                  color: 'blue'
                }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.2 }}
                  className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 text-center"
                >
                  <p className={`text-4xl font-bold mb-2 ${
                    item.color === 'green' ? 'text-green-600' :
                    item.color === 'red' ? 'text-red-600' :
                    'text-blue-600'
                  }`}>
                    {item.stat}
                  </p>
                  <p className="text-gray-900 font-semibold mb-2">{item.label}</p>
                  <p className="text-sm text-gray-500">{item.source}</p>
                </motion.div>
              ))}
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-8 border border-purple-200">
              <blockquote className="text-xl text-gray-800 italic text-center mb-4">
                "Use the inevitable to your advantage"
              </blockquote>
              <p className="text-center text-gray-600">- Napoleon Bonaparte</p>
            </div>

            <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-200">
              <h3 className="text-xl font-bold text-gray-900 mb-3">The AI Revolution in Marketing</h3>
              <p className="text-gray-700 leading-relaxed">
                In 2-5 years AI will have exceeded every human marketer's abilities. 
                What will be missing is a platform that combines AI's abilities with automating 
                the actual execution of campaigns across all platforms.
              </p>
              <p className="text-gray-900 font-bold mt-4">
                Now is the time to position ourselves to become the defacto platform in that inevitable future.
              </p>
            </div>
          </motion.div>
        </div>
      )
    },

    // Use of Funds Slide
    {
      id: 'use-of-funds',
      content: (
        <div className="flex flex-col h-full px-8 py-12">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-8"
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-4xl font-bold text-gray-900">
                Use of Funds
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-8 border border-purple-200"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                    50%
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Distribution</h3>
                </div>
                <ul className="space-y-3">
                  {[
                    'Professional grade viral videos',
                    'Media buyer',
                    'Ad testing budget',
                    'Grow cold DM team (6 → 15)',
                    'Head of growth'
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-8 border border-green-200"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                    50%
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Operations</h3>
                </div>
                <div className="bg-white rounded-lg p-4 border border-green-200">
                  <p className="text-xl text-gray-700">6 months of salaries</p>
                  <p className="text-gray-600 mt-2">Ensuring runway for core team to execute on vision</p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      )
    },

    // Fundraising Goal Slide
    {
      id: 'fundraising',
      content: (
        <div className="flex flex-col items-center justify-center h-full text-center px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="space-y-8 max-w-4xl"
          >
            <div className="inline-flex items-center justify-center p-3 bg-gradient-to-r from-purple-600/10 to-blue-600/10 rounded-2xl mb-4">
              <Rocket className="w-12 h-12 text-purple-600" />
            </div>

            <h2 className="text-5xl font-bold text-gray-900">
              Fundraising Goal
            </h2>

            <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-8 text-white">
              <p className="text-4xl font-bold mb-4">$500,000</p>
              <p className="text-xl mb-2">Pre-seed at $7M post-money valuation</p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">6-Month Target</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <p className="text-3xl font-bold text-green-600">$2M ARR</p>
                  <p className="text-gray-600">Target Revenue</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                  <p className="text-3xl font-bold text-purple-600">$40-60M</p>
                  <p className="text-gray-600">Seed Valuation</p>
                </div>
              </div>
            </div>

            <p className="text-gray-600 italic">
              *$2M ARR was the average traction required for a $40M seed round in 2025 for B2B AI products.*
            </p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <Button
                size="lg"
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-8 py-6 text-xl rounded-xl shadow-lg"
                onClick={() => window.open('https://calendly.com/airliusinc/munger-ai-demo-clone', '_blank')}
              >
                <Calendar className="w-6 h-6 mr-2" />
                Schedule a Call
              </Button>
            </motion.div>
          </motion.div>
        </div>
      )
    }
  ];

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && currentSlide > 0) {
        setCurrentSlide(currentSlide - 1);
      } else if (e.key === 'ArrowRight' && currentSlide < slides.length - 1) {
        setCurrentSlide(currentSlide + 1);
      } else if (e.key === ' ') {
        e.preventDefault();
        if (currentSlide < slides.length - 1) {
          setCurrentSlide(currentSlide + 1);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlide, slides.length]);

  // Touch handling
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    touchEndX.current = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX.current;
    
    if (Math.abs(diff) > 50) {
      if (diff > 0 && currentSlide < slides.length - 1) {
        setCurrentSlide(currentSlide + 1);
      } else if (diff < 0 && currentSlide > 0) {
        setCurrentSlide(currentSlide - 1);
      }
    }
  };

  // Auto-play functionality
  useEffect(() => {
    if (isAutoPlaying && currentSlide < slides.length - 1) {
      const timer = setTimeout(() => {
        setCurrentSlide(currentSlide + 1);
      }, 5000);
      return () => clearTimeout(timer);
    } else if (isAutoPlaying && currentSlide === slides.length - 1) {
      setIsAutoPlaying(false);
    }
  }, [currentSlide, isAutoPlaying, slides.length]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white overflow-hidden">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-20 bg-white/80 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Rocket className="w-6 h-6 text-purple-600" />
            <span className="font-bold text-xl bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              MUNGER AI
            </span>
          </div>
          
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsAutoPlaying(!isAutoPlaying)}
            >
              {isAutoPlaying ? 'Pause' : 'Auto-play'}
            </Button>
            
            <div className="text-sm text-gray-600">
              {currentSlide + 1} / {slides.length}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div 
        className="relative h-screen pt-16"
        ref={containerRef}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className="relative h-full flex overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              className="absolute inset-0 flex items-center justify-center"
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.3 }}
            >
              <div className="w-full max-w-7xl mx-auto h-full overflow-y-auto">
                {slides[currentSlide].content}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation */}
        <div className="absolute bottom-8 left-0 right-0 flex items-center justify-center gap-4 z-10">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))}
            disabled={currentSlide === 0}
            className="bg-white/80 backdrop-blur-sm"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>

          <div className="flex gap-2">
            {slides.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentSlide(idx)}
                className={`w-2 h-2 rounded-full transition-all ${
                  idx === currentSlide 
                    ? 'w-8 bg-gradient-to-r from-purple-600 to-blue-600' 
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>

          <Button
            variant="outline"
            size="icon"
            onClick={() => setCurrentSlide(Math.min(slides.length - 1, currentSlide + 1))}
            disabled={currentSlide === slides.length - 1}
            className="bg-white/80 backdrop-blur-sm"
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Keyboard hints */}
      <div className="absolute bottom-4 right-4 text-xs text-gray-500 bg-white/80 backdrop-blur-sm rounded-lg px-3 py-2">
        Use ← → arrows or swipe to navigate
      </div>
    </div>
  );
};

export default InvestorDeck;